/**
 * Project Service - API calls for project CRUD operations
 *
 * PURPOSE:
 * Provides a clean interface for project API operations with automatic hydration.
 * All methods return fully-hydrated ProjectData ready for use in the frontend.
 *
 * PATTERN:
 * - Returns `data | null` (null on any error)
 * - Uses logger for error reporting
 * - Hydrates API responses before returning
 *
 * API ENDPOINTS:
 * - GET    /api/projects           → List user's projects (metadata only)
 * - POST   /api/projects           → Create project
 * - GET    /api/projects/:id       → Get project with inputs
 * - PUT    /api/projects/:id       → Update project
 * - DELETE /api/projects/:id       → Delete project
 */

import type { ProjectInfo, ProjectInputs, ProjectMetadataInfo } from '@shared/models';
import type { ProjectData, ProjectMetadata } from '~/hooks/useProjectStore';
import { logger } from '~/services/logging';
import { dehydrateProjectInputs, hydrateProject } from './hydration-service';

// ============================================
// Types
// ============================================

export interface CreateProjectParams {
  name: string;
  clientName?: string;
  inputs?: ProjectInputs;
  targetCompletionDate?: string;
}

export interface UpdateProjectParams {
  name?: string;
  clientName?: string;
  inputs?: ProjectInputs;
  targetCompletionDate?: string | null;
  archivedAt?: string | null;
}

// ============================================
// API Methods
// ============================================

/**
 * Get all projects for the current user (metadata only, sorted by most recent)
 * Returns list of project metadata for display in project list UI
 */
export async function getProjects(): Promise<ProjectMetadata[] | null> {
  try {
    const response = await fetch('/api/projects');

    if (!response.ok) {
      logger.L2.debug('[ProjectService] Failed to fetch projects', {
        context: 'api:projects',
        status: response.status,
      });
      return null;
    }

    const { projects } = (await response.json()) as { projects: ProjectMetadataInfo[] };

    // Map to frontend metadata type
    return projects.map((p) => ({
      id: p.id,
      name: p.name,
      clientName: p.clientName,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      targetCompletionDate: p.targetCompletionDate,
      archivedAt: p.archivedAt,
      hasInputs: p.hasInputs,
      configurationCount: p.configurationCount,
      favoriteCount: p.favoriteCount,
      isTestingProject: p.isTestingProject,
      testScenarioId: p.testScenarioId,
    }));
  } catch (error) {
    logger.L2.debug('[ProjectService] Error fetching projects', {
      context: 'api:projects',
      error: String(error),
    });
    return null;
  }
}

/**
 * Get testing projects only (admin only)
 * Returns list of testing project metadata for values testing UI
 */
export async function getTestingProjects(): Promise<ProjectMetadata[] | null> {
  try {
    const response = await fetch('/api/projects/testing');

    if (!response.ok) {
      if (response.status === 403) {
        logger.L2.debug('[ProjectService] Access denied for testing projects', {
          context: 'api:projects',
        });
      } else {
        logger.L2.debug('[ProjectService] Failed to fetch testing projects', {
          context: 'api:projects',
          status: response.status,
        });
      }
      return null;
    }

    const { projects } = (await response.json()) as { projects: ProjectMetadataInfo[] };

    // Map to frontend metadata type
    return projects.map((p) => ({
      id: p.id,
      name: p.name,
      clientName: p.clientName,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      targetCompletionDate: p.targetCompletionDate,
      archivedAt: p.archivedAt,
      hasInputs: p.hasInputs,
      configurationCount: p.configurationCount,
      favoriteCount: p.favoriteCount,
      isTestingProject: p.isTestingProject,
      testScenarioId: p.testScenarioId,
    }));
  } catch (error) {
    logger.L2.debug('[ProjectService] Error fetching testing projects', {
      context: 'api:projects',
      error: String(error),
    });
    return null;
  }
}

/**
 * Get a single project by ID with full data (hydrated)
 * Returns fully-hydrated ProjectData with calculated fields rebuilt
 */
export async function getProject(projectId: string): Promise<ProjectData | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}`);

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ProjectService] Project not found', {
          context: 'api:projects',
          projectId,
        });
      } else {
        logger.L2.debug('[ProjectService] Failed to fetch project', {
          context: 'api:projects',
          projectId,
          status: response.status,
        });
      }
      return null;
    }

    const { project } = (await response.json()) as { project: ProjectInfo };

    // Hydrate the project (rebuild calculated data)
    return hydrateProject(project);
  } catch (error) {
    logger.L2.debug('[ProjectService] Error fetching project', {
      context: 'api:projects',
      projectId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Create a new project
 * Returns the created project (hydrated)
 */
export async function createProject(params: CreateProjectParams): Promise<ProjectData | null> {
  try {
    const response = await fetch('/api/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      logger.L2.debug('[ProjectService] Failed to create project', {
        context: 'api:projects',
        status: response.status,
        name: params.name,
      });
      return null;
    }

    const { project } = (await response.json()) as { project: ProjectInfo };

    return hydrateProject(project);
  } catch (error) {
    logger.L2.debug('[ProjectService] Error creating project', {
      context: 'api:projects',
      error: String(error),
    });
    return null;
  }
}

/**
 * Update an existing project
 * Returns the updated project (hydrated)
 */
export async function updateProject(
  projectId: string,
  params: UpdateProjectParams
): Promise<ProjectData | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ProjectService] Project not found for update', {
          context: 'api:projects',
          projectId,
        });
      } else {
        logger.L2.debug('[ProjectService] Failed to update project', {
          context: 'api:projects',
          projectId,
          status: response.status,
        });
      }
      return null;
    }

    const { project } = (await response.json()) as { project: ProjectInfo };

    return hydrateProject(project);
  } catch (error) {
    logger.L2.debug('[ProjectService] Error updating project', {
      context: 'api:projects',
      projectId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Update project inputs only (convenience method)
 * Extracts inputs from ProjectData and sends to API
 */
export async function updateProjectInputs(project: ProjectData): Promise<ProjectData | null> {
  const inputs = dehydrateProjectInputs(project);
  return updateProject(project.metadata.id, { inputs });
}

/**
 * Delete a project
 * Returns true on success, false on failure
 */
export async function deleteProject(projectId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/projects/${projectId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ProjectService] Project not found for delete', {
          context: 'api:projects',
          projectId,
        });
      } else {
        logger.L2.debug('[ProjectService] Failed to delete project', {
          context: 'api:projects',
          projectId,
          status: response.status,
        });
      }
      return false;
    }

    return true;
  } catch (error) {
    logger.L2.debug('[ProjectService] Error deleting project', {
      context: 'api:projects',
      projectId,
      error: String(error),
    });
    return false;
  }
}

/**
 * Delete a testing project (super admin only)
 * Uses a separate endpoint that bypasses the normal testing project protection
 * Returns true on success, false on failure
 */
export async function deleteTestingProject(projectId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/projects/testing/${projectId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ProjectService] Testing project not found for delete', {
          context: 'api:projects',
          projectId,
        });
      } else if (response.status === 403) {
        logger.L2.debug('[ProjectService] Access denied for deleting testing project', {
          context: 'api:projects',
          projectId,
        });
      } else {
        logger.L2.debug('[ProjectService] Failed to delete testing project', {
          context: 'api:projects',
          projectId,
          status: response.status,
        });
      }
      return false;
    }

    return true;
  } catch (error) {
    logger.L2.debug('[ProjectService] Error deleting testing project', {
      context: 'api:projects',
      projectId,
      error: String(error),
    });
    return false;
  }
}

// ============================================
// Convenience Methods
// ============================================

/**
 * Save a complete project (create or update based on whether it exists)
 * This is useful for syncing local state to backend
 */
export async function saveProject(project: ProjectData): Promise<ProjectData | null> {
  const inputs = dehydrateProjectInputs(project);

  // Try to update first (most common case)
  const updateResult = await updateProject(project.metadata.id, {
    name: project.metadata.name,
    clientName: project.metadata.clientName,
    inputs,
  });

  if (updateResult) {
    return updateResult;
  }

  // If update failed (404), create new project
  return createProject({
    name: project.metadata.name,
    clientName: project.metadata.clientName,
    inputs,
  });
}

/**
 * Update project metadata only (name, client, dates)
 * Does not touch inputs - useful for edit modal
 */
export async function updateProjectMetadata(
  projectId: string,
  params: Omit<UpdateProjectParams, 'inputs'>
): Promise<ProjectData | null> {
  return updateProject(projectId, params);
}

/**
 * Archive a project (set archivedAt timestamp)
 */
export async function archiveProject(projectId: string): Promise<ProjectData | null> {
  return updateProject(projectId, { archivedAt: new Date().toISOString() });
}

/**
 * Unarchive a project (clear archivedAt timestamp)
 */
export async function unarchiveProject(projectId: string): Promise<ProjectData | null> {
  return updateProject(projectId, { archivedAt: null });
}

/**
 * Clone a project with optional configuration inclusion
 * Currently implemented client-side (loads source, creates new with cloned data)
 */
export interface CloneProjectOptions {
  newName: string;
  includeConfigurations: boolean;
}

export async function cloneProject(
  sourceProjectId: string,
  options: CloneProjectOptions
): Promise<ProjectData | null> {
  try {
    // Load the source project
    const sourceProject = await getProject(sourceProjectId);
    if (!sourceProject) {
      logger.L2.debug('[ProjectService] Source project not found for clone', {
        context: 'api:projects',
        projectId: sourceProjectId,
      });
      return null;
    }

    // Prepare inputs from the source project
    const inputs = dehydrateProjectInputs(sourceProject);

    // Create the cloned project with the source's inputs
    const clonedProject = await createProject({
      name: options.newName,
      clientName: sourceProject.metadata.clientName,
      inputs,
    });

    if (!clonedProject) {
      logger.L2.debug('[ProjectService] Failed to create cloned project', {
        context: 'api:projects',
        sourceProjectId,
      });
      return null;
    }

    // If includeConfigurations is true, we need to clone configurations
    // This will be handled by the store action to also clone configurations via ConfigurationService

    logger.L2.debug('[ProjectService] Project cloned successfully', {
      context: 'api:projects',
      sourceProjectId,
      newProjectId: clonedProject.metadata.id,
      includeConfigurations: options.includeConfigurations,
    });

    return clonedProject;
  } catch (error) {
    logger.L2.debug('[ProjectService] Error cloning project', {
      context: 'api:projects',
      sourceProjectId,
      error: String(error),
    });
    return null;
  }
}

// ============================================
// Export as namespace-like object (optional)
// ============================================

export const ProjectService = {
  getProjects,
  getTestingProjects,
  getProject,
  createProject,
  updateProject,
  updateProjectInputs,
  updateProjectMetadata,
  archiveProject,
  unarchiveProject,
  cloneProject,
  deleteProject,
  deleteTestingProject,
  saveProject,
};
